import React from 'react';
import {
  Paper,
  IconButton,
  Grid,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { connect } from 'react-redux';
import { DeviceService } from '../../services';
import { BaseModalStyles } from './common/modalUtils';
import translations from '../../assets/translations';
import { Trans } from 'react-i18next';
import * as filterUtils from '../../common/filterUtils';
import { SnackbarUtils } from '../StyledSnackbarProvider';
import { Store, CustomerConfiguration } from '../../common/types';

/** Maximum polling attempts (3 s × 200 = 10 minutes) */
const MAX_POLL_ATTEMPTS = 200;
const POLL_INTERVAL_MS = 3000;

type Props = WithStyles<any> & {
  closeModal: () => void;
  config?: CustomerConfiguration;
};

type State = {
  exportDataType: string;
  loading: boolean;
  exportId: string | null;
  errorMessage: string | null;
};

class ExportDeviceListMC extends React.Component<Props, State> {
  state: State = {
    exportDataType: '1',
    loading: false,
    exportId: null,
    errorMessage: null,
  };

  private deviceService!: any;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollAttempts = 0;
  private downloadSnackId: string | number | null = null;

  componentDidMount = async (): Promise<void> => {
    this.deviceService = await DeviceService.create();
  };

  componentWillUnmount = (): void => {
    this.stopPolling();
    this.closeDownloadSnack();
  };

  // ─── Export submission ──────────────────────────────────────────────────────

  exportDeviceList = async (): Promise<void> => {
    this.setState({ loading: true, exportId: null, errorMessage: null });

    // Show the blue "in progress" snackbar immediately on click and keep it
    // visible until the export finishes (download/failure/timeout).
    // Use a longer auto-hide (90s) so it stays visible during long exports;
    // it is still closed explicitly once the export completes.
    this.downloadSnackId = SnackbarUtils.download(
      'Downloading export_csv_for_update_contract.csv',
      undefined,
      { autoHideDuration: 90000 },
    ) ?? null;

    try {
      const exportAll = this.state.exportDataType === '1';
      let queryParams: Record<string, string> | undefined;

      if (!exportAll) {
        const filterConditions = filterUtils.createFilterAndMetaDataFromUrl(window.location.search);
        queryParams = filterUtils.createParamsFromFilter(filterConditions.filters) as Record<string, string>;
      }

      const response = await this.deviceService.device.createDeviceExport({
        exportAll,
        queryParams: queryParams || undefined,
      });

      const { exportId } = response.data;
      this.setState({ exportId });
      this.startPolling(exportId);
    } catch (err) {
      console.error('Export creation failed:', err);
      this.closeDownloadSnack();
      this.setState({ loading: false, errorMessage: 'Failed to start export. Please try again.' });
    }
  };

  /** Close the persistent "in progress" snackbar if it is still open. */
  closeDownloadSnack = (): void => {
    if (this.downloadSnackId !== null) {
      SnackbarUtils.close(this.downloadSnackId);
      this.downloadSnackId = null;
    }
  };

  // ─── Polling ────────────────────────────────────────────────────────────────

  startPolling = (exportId: string): void => {
    this.pollAttempts = 0;
    this.schedulePoll(exportId);
  };

  schedulePoll = (exportId: string): void => {
    this.pollTimer = setTimeout(() => this.poll(exportId), POLL_INTERVAL_MS);
  };

  poll = async (exportId: string): Promise<void> => {
    this.pollAttempts += 1;

    if (this.pollAttempts > MAX_POLL_ATTEMPTS) {
      this.stopPolling();
      this.closeDownloadSnack();
      this.setState({ loading: false, errorMessage: 'Export timed out. Please try again.' });
      return;
    }

    try {
      const response = await this.deviceService.device.getDeviceExportInfo(exportId);
      const { status, s3Url, s3Key } = response.data;

      if (status === 'DONE' && (s3Key || s3Url)) {
        this.stopPolling();
        await this.triggerDownload(s3Key, s3Url);
        this.setState({ loading: false });
        this.props.closeModal();
      } else if (status === 'FAILED') {
        this.stopPolling();
        this.closeDownloadSnack();
        this.setState({ loading: false, errorMessage: 'Export failed on the server. Please try again.' });
      } else {
        // Still PENDING or PROCESSING – schedule next poll
        this.schedulePoll(exportId);
      }
    } catch (err) {
      console.error('Export poll error:', err);
      // Keep polling on transient errors
      this.schedulePoll(exportId);
    }
  };

  stopPolling = (): void => {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  };

  /**
   * Download the exported CSV using Cognito credentials via the S3 SDK
   * (same mechanism as the docs "mSafety SDK releases" downloads), instead of
   * navigating the browser directly to the S3 URL which is not publicly readable
   * and therefore returns AccessDenied.
   */
  triggerDownload = async (s3Key: string | null, s3Url: string | null): Promise<void> => {
    const fileName = 'export_csv_for_update_contract.csv';
    const bucket = this.props.config?.bucket;
    const key = s3Key || this.extractKeyFromUrl(s3Url);

    if (!bucket || !key) {
      this.closeDownloadSnack();
      this.setState({ errorMessage: 'Failed to download export: missing file location.' });
      return;
    }

    try {
      const { Body, ContentType } = await this.deviceService.authenticationServiceProvider.downloadFile({
        params: { Bucket: bucket, Key: key },
      });
      const blob = new Blob([Body as Uint8Array], { type: ContentType });

      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      this.closeDownloadSnack();
      SnackbarUtils.success(`Successfully downloaded ${fileName}`);
    } catch (err) {
      console.error('Export download failed:', err);
      this.closeDownloadSnack();
      SnackbarUtils.error(`Failed to download ${fileName}`);
      this.setState({ errorMessage: 'Failed to download export. Please try again.' });
    }
  };

  /** Derive the S3 object key from a full https S3 URL (bucket-in-path style). */
  extractKeyFromUrl = (s3Url: string | null): string | null => {
    if (!s3Url) return null;
    const marker = 'downloads/';
    const idx = s3Url.indexOf(marker);
    return idx > -1 ? s3Url.slice(idx) : null;
  };

  // ─── Handlers ───────────────────────────────────────────────────────────────

  handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({ exportDataType: event.target.value });
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  render = (): React.ReactNode => {
    const { loading, errorMessage } = this.state;

    return (
      <Paper tabIndex={-1} square className={this.props.classes.paper}>
        <IconButton
          style={{ ...BaseModalStyles().closeButton }}
          onClick={this.props.closeModal}
          aria-label="close"
          size="large"
          disabled={loading}
        >
          <CloseOutlined />
        </IconButton>
        <Grid container style={{ flexDirection: 'column', flexWrap: 'nowrap', height: '100%' }}>
          <Grid container spacing={4} style={{ flexDirection: 'column', flexWrap: 'nowrap', flex: '1 0 auto' }}>
            <Grid item xs={12} style={{ flex: 1 }}>
              <Typography variant="h3">
                {translations.en.translation.modals.exportDeciceList.title}
              </Typography>
            </Grid>
            <Grid item xs={12} style={{ flex: 1, minHeight: '15%' }}>
              <Typography variant="body1">
                {translations.en.translation.modals.exportDeciceList.description}
              </Typography>
            </Grid>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'left' }}>
              <FormControl>
                <FormLabel>Select set of devices</FormLabel>
                <RadioGroup defaultValue={this.state.exportDataType} onChange={this.handleChange}>
                  <FormControlLabel value="1" control={<Radio />} label="Export ALL devices" />
                  <FormControlLabel value="2" control={<Radio />} label="Export the found devices" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {errorMessage && (
              <Grid item xs={12}>
                <Alert severity="error">{errorMessage}</Alert>
              </Grid>
            )}
          </Grid>
          <Grid item>
            <Button
              color="secondary"
              variant="contained"
              disabled={loading}
              onClick={this.exportDeviceList}
            >
              {loading
                ? <CircularProgress size={24} color="inherit" />
                : <Trans>action.exportToCsv</Trans>}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  };
}

const mapStateToProps = ({ userStore }: Store) => ({
  config: userStore.config,
});

export default withStyles(BaseModalStyles)(connect(mapStateToProps)(ExportDeviceListMC));
